/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { CatalogClient } from '@mainstage/catalog-client';
import { Entity } from '@mainstage/catalog-model';
import {
  catalogApiRef,
  entityRouteRef,
  starredEntitiesApiRef,
} from '@mainstage/plugin-catalog-react';
import {
  createComponentRouteRef,
  viewTechDocRouteRef,
  rootRouteRef,
  myProjectsRouteRef,
  registerComponentRouteRef,
  templatesRootRouteRef,
  blueprintsRootRouteRef,
  adminCatalogRouteRef,
  templateIndexRouteRef,
  taskListRouteRef,
  templateEditorRouteRef,
  actionsRouteRef,
  taskRouteRef,
  iamRouteRef,
} from './routes';
import {
  createApiFactory,
  createComponentExtension,
  createPlugin,
  createRoutableExtension,
  discoveryApiRef,
  fetchApiRef,
  storageApiRef,
} from '@mainstage/core-plugin-api';
import {
  panelCatalogApiRef,
  PanelCatalogClient,
  systemLifecycleApiRef,
  SystemLifecycleClient,
} from './api';
import { DefaultStarredEntitiesApi, documentsApiRef } from './apis';
import { DefaultCatalogPageProps } from './components/CatalogPage';
import { DependencyOfComponentsCardProps } from './components/DependencyOfComponentsCard';
import { DependsOnComponentsCardProps } from './components/DependsOnComponentsCard';
import { HasSystemsCardProps } from './components/HasSystemsCard';
import { RelatedEntitiesCardProps } from './components/RelatedEntitiesCard';
import { extensionApiRef } from './CatalogExtensionApiRef';
import { CatalogClientExtension } from '@agilelab/plugin-wb-catalog-extension-common';
import { DocumentsClient } from '@agilelab/plugin-wb-documents-common';
import { OwnershipCardProps } from './components/OwnershipCard/OwnershipCard';

/** @public */
export const catalogPlugin = createPlugin({
  id: 'catalog',
  apis: [
    createApiFactory({
      api: catalogApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new CatalogClient({ discoveryApi, fetchApi }),
    }),
    createApiFactory({
      api: extensionApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new CatalogClientExtension({ discoveryApi, fetchApi }),
    }),
    createApiFactory({
      api: documentsApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new DocumentsClient({ discoveryApi, fetchApi }),
    }),
    createApiFactory({
      api: starredEntitiesApiRef,
      deps: { storageApi: storageApiRef },
      factory: ({ storageApi }) =>
        new DefaultStarredEntitiesApi({ storageApi }),
    }),
    createApiFactory({
      api: panelCatalogApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new PanelCatalogClient({ discoveryApi, fetchApi }),
    }),
    createApiFactory({
      api: systemLifecycleApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new SystemLifecycleClient(discoveryApi, fetchApi),
    }),
  ],
  routes: {
    catalogIndex: rootRouteRef,
    catalogEntity: entityRouteRef,
    adminCatalogRouteRef: adminCatalogRouteRef,
  },
  externalRoutes: {
    registerComponent: registerComponentRouteRef,
    createComponent: createComponentRouteRef,
    viewTechDoc: viewTechDocRouteRef,
    templatesRootRouteRef: templatesRootRouteRef,
    blueprintsRootRouteRef: blueprintsRootRouteRef,
    templateIndexRouteRef: templateIndexRouteRef,
    iamRouteRef: iamRouteRef,
    taskListRouteRef: taskListRouteRef,
    templateEditorRouteRef: templateEditorRouteRef,
    actionsRouteRef: actionsRouteRef,
    taskRouteRef: taskRouteRef,
  },
});

/** @public */
export const CatalogIndexPage: (props: DefaultCatalogPageProps) => JSX.Element =
  catalogPlugin.provide(
    createRoutableExtension({
      name: 'CatalogIndexPage',
      component: () =>
        import('./components/CatalogPage').then(m => m.CatalogPage),
      mountPoint: rootRouteRef,
    }),
  );

/** @public */
export const AdministratorCatalogIndexPage: (
  props: DefaultCatalogPageProps,
) => JSX.Element = catalogPlugin.provide(
  createRoutableExtension({
    name: 'AdministratorCatalogIndexPage',
    component: () =>
      import('./components/CatalogPage').then(m => m.CatalogPage),
    mountPoint: adminCatalogRouteRef,
  }),
);

export const MyProjectsPage = catalogPlugin.provide(
  createRoutableExtension({
    name: 'MyProjectsPage',
    component: () => import('./components/MyProjectsPage').then(m => m.Router),
    mountPoint: myProjectsRouteRef,
  }),
);

/** @public */
export const CatalogEntityPage: () => JSX.Element = catalogPlugin.provide(
  createRoutableExtension({
    name: 'CatalogEntityPage',
    component: () =>
      import('./components/CatalogEntityPage').then(m => m.CatalogEntityPage),
    mountPoint: entityRouteRef,
  }),
);

/** @public */
export const EntityLinksCard = catalogPlugin.provide(
  createComponentExtension({
    name: 'EntityLinksCard',
    component: {
      lazy: () =>
        import('./components/EntityLinksCard').then(m => m.EntityLinksCard),
    },
  }),
);

/** @public */
export const EntityHasSystemsCard: (props: HasSystemsCardProps) => JSX.Element =
  catalogPlugin.provide(
    createComponentExtension({
      name: 'EntityHasSystemsCard',
      component: {
        lazy: () =>
          import('./components/HasSystemsCard').then(m => m.HasSystemsCard),
      },
    }),
  );

/** @public */
export const EntityHasComponentsCard: () => JSX.Element = catalogPlugin.provide(
  createComponentExtension({
    name: 'EntityHasComponentsCard',
    component: {
      lazy: () =>
        import('./components/HasComponentsCard').then(m => m.HasComponentsCard),
    },
  }),
);

/** @public */
export const EntityHasSubcomponentsCard: () => JSX.Element =
  catalogPlugin.provide(
    createComponentExtension({
      name: 'EntityHasSubcomponentsCard',
      component: {
        lazy: () =>
          import('./components/HasSubcomponentsCard').then(
            m => m.HasSubcomponentsCard,
          ),
      },
    }),
  );

/** @public */
export const EntityHasResourcesCard: () => JSX.Element = catalogPlugin.provide(
  createComponentExtension({
    name: 'EntityHasResourcesCard',
    component: {
      lazy: () =>
        import('./components/HasResourcesCard').then(m => m.HasResourcesCard),
    },
  }),
);

/** @public */
export const EntityDependsOnComponentsCard: (
  props: DependsOnComponentsCardProps,
) => JSX.Element = catalogPlugin.provide(
  createComponentExtension({
    name: 'EntityDependsOnComponentsCard',
    component: {
      lazy: () =>
        import('./components/DependsOnComponentsCard').then(
          m => m.DependsOnComponentsCard,
        ),
    },
  }),
);

/** @public */
export const EntityDependencyOfComponentsCard: (
  props: DependencyOfComponentsCardProps,
) => JSX.Element = catalogPlugin.provide(
  createComponentExtension({
    name: 'EntityDependencyOfComponentsCard',
    component: {
      lazy: () =>
        import('./components/DependencyOfComponentsCard').then(
          m => m.DependencyOfComponentsCard,
        ),
    },
  }),
);

/** @public */
export const EntityDependsOnResourcesCard: () => JSX.Element =
  catalogPlugin.provide(
    createComponentExtension({
      name: 'EntityDependsOnResourcesCard',
      component: {
        lazy: () =>
          import('./components/DependsOnResourcesCard').then(
            m => m.DependsOnResourcesCard,
          ),
      },
    }),
  );

export const EntitySystemDiagramCard = catalogPlugin.provide(
  createComponentExtension({
    name: 'EntitySystemDiagramCard',
    component: {
      lazy: () =>
        import('./components/SystemDiagramCard').then(m => m.SystemDiagramCard),
    },
  }),
);

/** @public */
export const EntityUserProfileCard: () => JSX.Element = catalogPlugin.provide(
  createComponentExtension({
    name: 'EntityUserProfileCard',
    component: {
      lazy: () =>
        import('./components/ProfileCard').then(m => m.UserProfileCard),
    },
  }),
);

/** @public */
export const EntityGroupProfileCard: () => JSX.Element = catalogPlugin.provide(
  createComponentExtension({
    name: 'EntityGroupProfileCard',
    component: {
      lazy: () =>
        import('./components/ProfileCard').then(m => m.GroupProfileCard),
    },
  }),
);

/** @public */
export const EntityOwnershipCard: (props: OwnershipCardProps) => JSX.Element =
  catalogPlugin.provide(
    createComponentExtension({
      name: 'EntityOwnershipCard',
      component: {
        lazy: () =>
          import('./components/OwnershipCard').then(m => m.OwnershipCard),
      },
    }),
  );

/** @public */
export const EntityMembersListCard: (props: OwnershipCardProps) => JSX.Element =
  catalogPlugin.provide(
    createComponentExtension({
      name: 'EntityMembersListCard',
      component: {
        lazy: () =>
          import('./components/MembersListCard').then(m => m.MembersListCard),
      },
    }),
  );

/** @public */
export const RelatedEntitiesCard: <T extends Entity>(
  props: RelatedEntitiesCardProps<T>,
) => JSX.Element = catalogPlugin.provide(
  createComponentExtension({
    name: 'RelatedEntitiesCard',
    component: {
      lazy: () =>
        import('./components/RelatedEntitiesCard').then(
          m => m.RelatedEntitiesCard,
        ),
    },
  }),
);
